import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FlexPlanEditor } from '../../components/FlexPlanEditor';
import { saveFlexPlan, getFlexPlan } from '../../services/flexPlans';
import { nextEditableMonth, isPlanLocked, splitFlexPricing } from '../../utils/flexPlan';
import { useFlexPricingRows } from '../../hooks/useFlexPricingRows';
import { type BrandKey } from '../../config/brands';

interface Props {
  enrollmentId: string;
  programId: string;
  /** Which brand's closure calendar greys out days. Absent reads as the default brand. */
  brandKey?: BrandKey;
  programSchedule?: string;
  /** `line_item_configs` ids this family declined — excluded from the per-day rate. */
  /** The optional line_item_config ids this enrollment pays for. */
  optInItems?: string[] | null;
}

/**
 * Admin editor for an enrollment's *next editable* flex month plan. Loads the existing
 * draft (if any), lets the admin adjust the days, and saves through flex_plan_save (which
 * re-enforces the lock and stamps updated_by). Self-contained — own queries + save — so it
 * can drop into the AdminEnrollments edit modal without threading state through it.
 */
export default function AdminFlexPlanSection({ enrollmentId, programId, brandKey, programSchedule, optInItems = null }: Props) {
  const queryClient = useQueryClient();
  const planMonth = nextEditableMonth();
  const locked = isPlanLocked(planMonth);

  const { data: flexPricing } = useFlexPricingRows(programId, programSchedule);
  const pricing = flexPricing
    ? (() => {
        const { perDayRate, monthlyAddOns } = splitFlexPricing(flexPricing.rows, { optIn: optInItems });
        return { perDay: perDayRate, addOns: monthlyAddOns, weekdays: flexPricing.weekdays };
      })()
    : undefined;

  const { data: plan, isLoading } = useQuery({
    queryKey: ['flex-plan', enrollmentId, planMonth],
    enabled: !!enrollmentId,
    queryFn: () => getFlexPlan(enrollmentId, planMonth),
  });

  const [dates, setDates] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Seed local edit state from the loaded plan once.
  const current = dates ?? plan?.dates ?? [];

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      await saveFlexPlan(enrollmentId, planMonth, current, {
        allowLocked: true,
        previousDates: plan?.dates ?? null,
        actorType: 'admin',
      });
      await queryClient.invalidateQueries({ queryKey: ['flex-plan', enrollmentId, planMonth] });
      setMsg('Saved.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">Next month&apos;s flex plan ({planMonth.slice(0, 7)})</p>
      {isLoading ? (
        <p className="text-xs text-gray-500">Loading plan…</p>
      ) : (
        <>
          <FlexPlanEditor
            planMonth={planMonth}
            perDayRate={pricing?.perDay ?? 0}
            dates={current}
            onChange={setDates}
            monthlyAddOns={pricing?.addOns ?? 0}
            scheduleWeekdays={pricing?.weekdays ?? []}
            brandKey={brandKey}
            readOnly={locked && plan?.status !== 'draft'}
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save plan'}
            </button>
            {plan?.status && <span className="text-xs text-gray-500">Status: {plan.status}</span>}
            {msg && <span className="text-xs text-gray-600">{msg}</span>}
          </div>
        </>
      )}
    </div>
  );
}
